import React, { useEffect, useState } from 'react'
import AutoSizer from 'react-virtualized-auto-sizer'
import { makeStyles } from '@material-ui/core'
import { TableVirtualizedHeader } from './table-virtualized-header'
import { sort, getObjValueByKey } from '../../../../../utils'
import {
    calculateColumnIndexToWidth,
    VIRTUALIZED_HEADER_ROW,
    DEFAULT_ROW_HEIGHT,
    defaultGetDerivedRows,
    defaultGetRowColumnsKey,
    defaultRowColumnKey
} from './table-virtualized-helpers'
import { TableVirtualizedRow } from './table-virtualized-row'
import cl from 'classnames'
import { getEvenRowBackground } from './styles'
import { scrollbarWidth as getScrollbarWidth } from '@xobotyi/scrollbar-width'
import { v1 as uuid } from 'uuid'
import { MultiGrid } from 'react-virtualized'

const useStyles = makeStyles(theme => ({
    root: {
        borderRadius: theme.borderRadius,
        border: theme.border,
        height: '100%',
        overflow: 'hidden',
        // fixes border corner styles in safari
        'will-change': 'transform'
    },
    innerRoot: {
        height: '100%'
    },
    gridCommon: {
        outline: 'none'
    },
    contentGrid: {
        // Vertical scroll needed to keep header and content width in sync(especially for mac).
        overflowY: 'scroll !important',
        overflowX: 'auto !important'
    },
    headerGrid: {
        overflowY: 'scroll !important',
        background: getEvenRowBackground(theme),
        borderBottom: theme.border,
        '&::-webkit-scrollbar': {
            background: 'transparent',
            height: 0
        }
    }
}))


function TableVirtualized(props) {
    const classes = useStyles()

    const {
        columns,
        rows,
        className,
        rowHeight = DEFAULT_ROW_HEIGHT,
        headerHeight = DEFAULT_ROW_HEIGHT,
        columnMinWidth = undefined,
        columnMaxWidth = undefined,

        getDerivedRows = defaultGetDerivedRows,
        specialRows = [],
        getRowColumnsKey = defaultGetRowColumnsKey,
        headerProps = undefined,

        noContentRenderer = undefined,
        onSectionRendered,
        customRenderer
    } = props

    const [lastCalculatedWidth, setLastCalculatedWidth] = useState(null)
    const [sortBy, setSortBy] = useState(null)
    const [columnKeyToColumnSizes, setColumnKeyToColumnSizes] = useState({})

    const columnKeyToColumnPure = Array.isArray(columns)
        ? { [defaultRowColumnKey]: columns }
        : columns
    let desc
    if (sortBy && rows && rows.length) {
        desc = !sortBy.dataType || sortBy.dataType === 'string' ? !sortBy.desc : sortBy.desc
    }
    const gridRef = React.createRef()
    const contentGridClass = `content-grid-${uuid()}`
    const isUsingPlaceholder = false
        
    const specialRowsMap = new Map([
        [VIRTUALIZED_HEADER_ROW, { renderer: renderHeader, rowHeight: headerHeight }],
        ...specialRows
    ])

    const columnKeyToColumn = Object.keys(columnKeyToColumnPure).reduce((acc, key) => {
        const columns = columnKeyToColumnPure[key]
        acc[key] = adjustColumnSizes(columns)
        return acc
    }, {})

    const sortedRows = (sortBy && rows && rows.length)
        ? sort(rows, sortBy.key, sortBy.dataType, !desc)
        : rows

    const derivedRows = getDerivedRows({ rows: [...sortedRows], props })
    derivedRows.unshift(VIRTUALIZED_HEADER_ROW)
    const computedRows = derivedRows

    const columnsKeyToWidth = Object.keys(columnKeyToColumnSizes).reduce((acc, key) => {
        const columnSizes = columnKeyToColumnSizes[key]
        acc[key] = Object.values(columnSizes).reduce((a, v) => a + v, 0)
        return acc
    }, {})

    const maxRowWidth = Math.max(...Object.values(columnsKeyToWidth))

    const uniqueKey = props.dataUniqueKey || 'id'

    function adjustColumnSizes(column) {
        return column.filter(Boolean).map(col => ({
            ...col,
            grow: col.grow !== undefined ? col.grow : col.width ? 0 : 1,
            minWidth: col.min || col.minWidth || columnMinWidth,
            maxWidth: col.max || col.maxWidth || columnMaxWidth
        }))
    }

    function getRowColumns({ rowData }) {
        const key = getRowColumnsKey({ rowData })
        return columnKeyToColumn[key]
    }

    function getRowColumnSizes({ rowData }) {
        const key = getRowColumnsKey({ rowData })
        return columnKeyToColumnSizes[key]
    }

    function getRowKey(rowData) {
        const { getUniqueRowKey } = props
        if (typeof getUniqueRowKey === 'function') {
            return getUniqueRowKey({ rowData })
        }
        return getObjValueByKey(rowData, uniqueKey)
    }

    function handleCalculateColumnIndexToWidth({ width }) {
        setLastCalculatedWidth(width)
        const columnKeyToColumnSizesReduced = Object.keys(columnKeyToColumn).reduce((acc, key) => {
            const columns = columnKeyToColumn[key]
            acc[key] = calculateColumnIndexToWidth({ columns, width, scrollbarWidth: getScrollbarWidth() })
            return acc
        }, {})
        setColumnKeyToColumnSizes(columnKeyToColumnSizesReduced)

        if (gridRef.current) {
            gridRef.current.recomputeGridSize()
        }
    }

    function onResize({ width, height }) {
        handleCalculateColumnIndexToWidth({ width })
    }

    function getColumnWidth({ index }) {
        const rowData = computedRows[index]
        const key = getRowColumnsKey({ rowData })
        return columnsKeyToWidth[key]
    }

    function getRowHeight({ index }) {
        const row = computedRows[index]
        const specialRow = specialRowsMap.get(row)
        if (specialRow) {
            return specialRow.rowHeight
        }
        return rowHeight
    }

    /**
     * @deprecated Use `getRowProps` instead
     *
     * @memberof TableVirtualized
     */
    function getDerivedRowClasses(rowData) {
        const { getRowClasses, rowProps } = props
        if (typeof getRowClasses === 'function') {
            return getRowClasses({ rowData })
        } else if (rowProps && rowProps.classes) {
            return rowProps.classes
        }
    }

    function getDerivedRowProps(rowData) {
        const { getRowProps, rowProps } = props
        if (typeof getRowProps === 'function') {
            return getRowProps({ rowData })
        }
        return { ...rowProps, classes: getDerivedRowClasses(rowData) }
    }

    function onHeaderColumnClick(column) {
        if (!column.sortable) {
            return
        }

        if (!sortBy || sortBy.key !== column.key) {
            setSortBy({
                key: column.key,
                dataType: column.dataType,
                desc: true
            })
            return
        }

        const newSortBy = sortBy.desc ? { key: sortBy.key, dataType: column.dataType, desc: false } : null
        setSortBy(newSortBy)
    }

    function getRowDataAndKey(rowIndex) {
        const rowData = computedRows[rowIndex]
        const rowKey = getRowKey(rowData)
        return { rowData, rowKey }
    }

    function renderHeader({ style, rowIndex }) {
        const rowData = computedRows[rowIndex]
        const columnIndexToWidth = getRowColumnSizes({ rowData })
        const columns = getRowColumns({ rowData })
        return (
            <TableVirtualizedHeader
                {...headerProps}
                key={'table-header-row'}
                style={style}
                columnIndexToWidth={columnIndexToWidth}
                columns={columns}
                rowHeight={rowHeight}
                sortBy={sortBy}
                onHeaderColumnClick={onHeaderColumnClick}
            />
        )
    }

    function renderRow({ rowData, rowIndex, style, rowKey }) {
        const rowProps = getDerivedRowProps(rowData)
        const columnIndexToWidth = getRowColumnSizes({ rowData })
        const columns = getRowColumns({ rowData })
        return (
            <TableVirtualizedRow
                {...rowProps}
                key={rowKey}
                rowKey={rowKey}
                style={style}
                rowData={rowData}
                columnIndexToWidth={columnIndexToWidth}
                columns={columns}
                customRenderer={customRenderer}
                rowIndex={rowIndex}
            />
        )
    }

    function renderRowHandler(gridRowProps) {
        const { rowData, rowKey } = getRowDataAndKey(gridRowProps.rowIndex)
        return renderRow({ ...gridRowProps, rowData, rowKey })
    }

    function rowRenderer(gridRowProps) {
        if (isUsingPlaceholder && gridRowProps.rowIndex === computedRows.length) {
            return null
        }

        const row = computedRows[gridRowProps.rowIndex]
        const specialRow = specialRowsMap.get(row)
        if (specialRow) {
            return specialRow.renderer(gridRowProps)
        }
        return renderRowHandler(gridRowProps)
    }

    function setGridRef(ref) {
        gridRef.current = ref
    }

    function renderer({ width, height }) {
        // prettier-ignore
        const rowCount = isUsingPlaceholder
            ? computedRows.length + 1
            : computedRows.length

        return (
            <MultiGrid
                cellRenderer={rowRenderer}
                columnWidth={getColumnWidth}
                columnCount={1}
                fixedRowCount={1}
                height={height}
                rowHeight={getRowHeight}
                ref={setGridRef}
                classNameBottomRightGrid={cl(classes.gridCommon, classes.contentGrid, contentGridClass)}
                classNameTopRightGrid={cl(classes.gridCommon, classes.headerGrid)}
                estimatedColumnSize={maxRowWidth}
                estimatedRowSize={rowHeight}
                overscanColumnCount={0}
                overscanRowCount={5}
                rowCount={rowCount}
                width={width}
                onSectionRendered={onSectionRendered}
                noContentRenderer={noContentRenderer}
                // rows/columns props needed to trigger re-render of the grid on columnKeyToColumn or computedRows change
                rows={computedRows}
                columns={columnKeyToColumn}
            />
        )
    }

    return (
        <div className={cl(className, classes.root)}>
            <div className={classes.innerRoot}>
                <AutoSizer onResize={onResize}>{renderer}</AutoSizer>
            </div>
        </div>
    )
}

export { TableVirtualized }
